import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { popupcontext } from "../../../context/Popupscontext";
import Display from "../../Helpers/Table/Table Actions/Display";
import { filtrationcontext } from "../../../context/Filtercontext";
import { useGetAllSnagsQuery } from "../../../Redux/service/Snag/Snag";
import Loader from "../../Helpers/Loader/Loader";
import trans from "../../../utils";
import { FloatButton, Input, Tag } from "antd";
import "../../../Styles/Mobile/irMobile.css";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import SnagLocationSelectorModal from "../../snagLocation/SnagLocationSelectorModal";
import FormSnag from "../FormSnag/FormSnag";
import usePermission from "../../../hooks/usePermission";

const SnagMobile = ({ userRole }) => {
  const { showDetails, setReloadData, reloadData, currentSnagId } =
    useContext(popupcontext);
  const { searchQuery, setSearchQuery } = useContext(filtrationcontext);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [preselectedLocation, setPreselectedLocation] = useState(null);
  const [snagForm, setSnagFormState] = useState(false);
  const [editingSnagId, setEditingSnagId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState([]);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);

  const setSnagForm = (value) => {
    setSnagFormState(value);
    if (!value) {
      setPreselectedLocation(null);
      setEditingSnagId(null);
      setIsEditMode(false);
    }
  };

  const { create } = usePermission("SNAG");

  const PAGE_SIZE = 20;
  const { data, isLoading, isFetching } = useGetAllSnagsQuery(
    {
      page,
      limit: PAGE_SIZE,
      search: searchQuery,
      status: null,
    },
    { skip: !page },
  );

  const total = data?.pagination?.total ?? 0;
  const hasMore = accumulated.length < total;

  useEffect(() => {
    if (!data?.data) return;
    if (page === 1) {
      setAccumulated(data.data);
    } else {
      setAccumulated((prev) => [...prev, ...(data.data || [])]);
    }
    loadingMoreRef.current = false;
  }, [data?.data, page]);

  useEffect(() => {
    setPage(1);
    setAccumulated([]);
  }, [searchQuery]);

  useEffect(() => {
    if (reloadData) {
      setPage(1);
      setAccumulated([]);
      setReloadData(false);
    }
  }, [reloadData, setReloadData]);

  useEffect(() => {
    if (!snagForm && preselectedLocation) {
      setPreselectedLocation(null);
    }
  }, [snagForm, preselectedLocation]);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !hasMore || isFetching) return;
    loadingMoreRef.current = true;
    setPage((p) => p + 1);
  }, [hasMore, isFetching]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || isFetching) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: null, rootMargin: "100px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, hasMore, isFetching]);

  const handleConfirmLocation = (locationData) => {
    setPreselectedLocation(locationData);
    setLocationModalOpen(false);
    setSnagFormState(true);
  };

  const handleCardClick = (record) => {
    showDetails("snag", record.id);
  };

  if (isLoading && page === 1) {
    return (
      <div className="ir-mobile-loader">
        <Loader />
      </div>
    );
  }

  return (
    <div className="ir-mobile">
      <div className="ir-mobile-scroll">
        <Input
          prefix={<SearchOutlined style={{ color: "#aaa" }} />}
          placeholder={trans("Search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="ir-mobile-search"
        />
        <div className="ir-mobile-cards">
          {accumulated.length === 0 && !isFetching ? (
            <div className="ir-mobile-empty">{trans("No Snags")}</div>
          ) : (
            accumulated.map((record) => {
              return (
                <div
                  key={record.id}
                  className="ir-mobile-card"
                  onClick={() => handleCardClick(record)}
                >
                  <div className="ir-mobile-card-header">
                    <div className="ir-mobile-card-header-left">
                      <span className="ir-mobile-card-number">
                        {record.snag_number}
                      </span>
                      {"  "}
                      {userRole != "consultant" && (
                        <span
                          style={{
                            color:
                              record?.snag_type === "EXTERNAL" ? "red" : "blue",
                          }}
                        >
                          {record?.snag_type === "EXTERNAL"
                            ? "External"
                            : "Internal"}
                        </span>
                      )}
                    </div>
                    <Tag color={record?.status_info?.color || "default"}>
                      {trans(record?.status_info?.label || "—")}
                    </Tag>
                  </div>
                  <div className="ir-mobile-card-body">
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Project")}
                      </span>
                      <span>{record?.project?.name ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Building")}
                      </span>
                      <span>{record?.zone?.name ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Level")}
                      </span>
                      <span>{record?.level?.name ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Room")}
                      </span>
                      <span>{record?.room?.name ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Sector")}
                      </span>
                      <span>{record?.sector_label ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Subsystem")}
                      </span>
                      <span>{record?.subsystem_label ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Severity")}
                      </span>
                      <Tag
                        color={
                          record?.severity === "MINOR"
                            ? "green"
                            : record?.severity === "MAJOR"
                              ? "yellow"
                              : record?.severity === "CRITICAL"
                                ? "red"
                                : "blue"
                        }
                      >
                        {trans(record?.severity || "—")}
                      </Tag>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Reported By")}
                      </span>
                      <span>{record?.reportedBy?.name}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Assigned To")}
                      </span>
                      <span>{record?.assignedTeam?.name}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div ref={sentinelRef} className="ir-mobile-sentinel" />
        {isFetching && page > 1 && (
          <div className="ir-mobile-loading-more">
            <Loader />
          </div>
        )}
      </div>
      {create && (
        <FloatButton
          type="primary"
          icon={<PlusOutlined />}
          className="ir-mobile-fab"
          onClick={() => setLocationModalOpen(true)}
          // tooltip={trans("Add new IR")}
        />
      )}
      <SnagLocationSelectorModal
        open={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        onConfirm={handleConfirmLocation}
      />
      <FormSnag
        snagForm={snagForm}
        setSnagForm={setSnagForm}
        preselectedLocation={preselectedLocation}
        editingSnagId={editingSnagId}
        isEditMode={isEditMode}
      />
      {currentSnagId && <Display snagId={currentSnagId} />}
    </div>
  );
};

export default SnagMobile;
